import useStyles from 'isomorphic-style-loader/useStyles';
import { useId, useRef, useState } from 'react';
import { useTimeout } from 'usehooks-ts';

import s from './logo-loader.scss';

const MIN_DISPLAY_MS = 2000;

const LOGO_PATH =
  'm18.173 24.705v-15.952c0-4.786-3.851-8.26-9.157-8.26-4.041 0-7.511 2.335-8.641 5.847-.047.445.069 1.251 1.376 1.655 1.434.441 1.963-.297 2.155-.784.744-2.139 2.75-3.522 5.11-3.522 3.216 0 5.551 2.151 5.551 5.113v1.27c0 .594-.793.981-1.542 1.147l-6.385 1.668c-4.778 1.088-6.64 2.939-6.64 6.602 0 4.257 3.831 7.474 8.926 7.511.069.001.136-.011.199-.038.238-.103.782-.453.782-1.504 0-1.049-.542-1.47-.78-1.605-.066-.037-.138-.053-.212-.054-3.082-.039-5.309-1.839-5.309-4.31 0-2.283 1.25-2.984 3.88-3.613 0 0 4.626-1.215 6.448-1.76.309-.092.616.142.61.464l-.016 1.011.013 5.61c0 .068.014.136.04.198.647 1.518 1.755 2.749 3.207 3.579.311.159.385-.104.385-.273';

export interface LogoLoaderProps {
  /**
   * How the stroke resets after each draw cycle.
   *
   * - `"reverse"` — retraces backward along the path.
   * - `"repeat"` — erases from the start while still drawing forward,
   *   creating a chasing loop.
   */
  exit?: 'repeat' | 'reverse';
  /**
   * Whether the async operation is still in progress. The minimum
   * display countdown begins once this becomes `false`, after which
   * the loader hides itself and fires `onComplete`.
   */
  loading?: boolean;
  /**
   * Optional text displayed below the logo animation.
   */
  message?: string;
  /**
   * Called once `loading` is `false` and the minimum display time has
   * elapsed.
   */
  onComplete?: () => void;
}

/**
 * Animated logo loader that draws the Anchore logomark via a CSS
 * `stroke-dashoffset` animation, as if the letter were being written.
 *
 * Guarantees a minimum display time so the animation always plays
 * through, even when the async operation resolves instantly.
 */
export const LogoLoader = ({
  exit = 'reverse',
  loading = true,
  message,
  onComplete,
}: LogoLoaderProps) => {
  useStyles(s);

  const messageId = useId();
  const [visible, setVisible] = useState(true);
  const startedAt = useRef(Date.now());

  const delay =
    !loading && visible
      ? Math.max(0, MIN_DISPLAY_MS - (Date.now() - startedAt.current))
      : null;

  useTimeout(() => {
    setVisible(false);
    onComplete?.();
  }, delay);

  if (!visible) return null;

  return (
    <div className={s.root}>
      <svg
        aria-label={message ? undefined : 'Loading'}
        aria-labelledby={message ? messageId : undefined}
        className={s.logo}
        fill="none"
        role="img"
        viewBox="-0.5 0 19.6 27.6"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          className={`${s.path} ${exit === 'repeat' ? s.repeat : s.reverse}`}
          d={LOGO_PATH}
        />
      </svg>

      {message && (
        <p className={s.message} id={messageId}>
          {message}
        </p>
      )}
    </div>
  );
};

export default LogoLoader;
